import React, { FC, ChangeEvent } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import { PhoneInput, IPhoneInputProps } from 'transfix-ui/components/PhoneInput'
import formatPhone, { unformatPhone } from 'utils/formatPhone'

type IBaseControllerProps = ControllerProps<React.ComponentType<IPhoneInputProps>>

export interface IControlledPhoneInputProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  PhoneInputProps?: Partial<IPhoneInputProps>
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledPhoneInput
 *   name='phone'
 *   control={control}
 *   PhoneInputProps={{
 *     label: 'Pickup Number',
 *     placeholder: '1234',
 *   }}
 * />
 * ```
 */
const ControlledPhoneInput: FC<IControlledPhoneInputProps> = ({
  control,
  name,
  defaultValue = '',
  PhoneInputProps = {},
}) => {
  const {
    autoComplete = 'off',
    className = '',
    clearable = true,
    containerRef,
    dataTest,
    disabled = false,
    invalid = false,
    label = '',
    large = false,
    placeholder = '(000) 000-0000',
    required = false,
    validationMessage,
  } = PhoneInputProps || {}
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ onBlur, onChange, value }) => {
        const formattedValue = formatPhone(value, { removeCountryCode: '+1 ' })

        const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
          onChange(unformatPhone(e.target.value))
        }

        const onClearAllHandler = (val: string) => {
          onChange(val)
        }

        return (
          <PhoneInput
            large={large}
            disabled={disabled}
            clearable={clearable}
            required={required}
            invalid={invalid}
            label={label}
            dataTest={dataTest}
            placeholder={placeholder}
            validationMessage={validationMessage}
            className={className}
            autoComplete={autoComplete}
            onChange={changeHandler}
            onClearAll={onClearAllHandler}
            onBlur={onBlur}
            value={formattedValue}
            containerRef={containerRef}
          />
        )
      }}
    />
  )
}

export default ControlledPhoneInput
